/**
 * Small Island Wines — tiger entry.
 *
 * Behaviour
 *   - The whole viewport is one <button>, so click, tap, Enter and Space all enter.
 *   - Escape also enters, because a full-screen surface that swallows Escape is rude.
 *   - Focus starts on that button and returns to the top of the page's tab order after.
 *   - Everything behind the entry is made inert while it is up.
 *   - prefers-reduced-motion removes the entry immediately with no transition.
 *
 * The overlay only ever displays when siw-tiger-entry-head.liquid has armed it.
 * If this file does not run, the head script's failsafe disarms it after 6 seconds.
 */
(() => {
  const root = document.documentElement;
  const entry = document.querySelector('[data-siw-entry]');
  if (!entry) return;

  // Not armed: either storage says this visitor has already entered, or the entry
  // is off. Remove the markup so nothing is left behind in the DOM.
  if (!root.classList.contains('siw-entry-armed')) {
    entry.remove();
    return;
  }

  const STORAGE_KEY = 'siw_entry_seen_v1';
  const frequency = entry.dataset.siwEntryFrequency || 'session';
  const surface = entry.querySelector('[data-siw-entry-enter]');
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // Tell the head failsafe that the entry is under management now.
  root.classList.add('siw-entry-ready');

  // Hide the rest of the page from assistive technology and from tabbing while
  // the entry is up. Visually it is already covered by an opaque surface.
  const inerted = [];
  for (const child of Array.from(document.body.children)) {
    if (child === entry || child.tagName === 'SCRIPT' || child.tagName === 'TEMPLATE') continue;
    if (child.hasAttribute('inert')) continue;
    child.setAttribute('inert', '');
    child.setAttribute('aria-hidden', 'true');
    inerted.push(child);
  }

  const releaseInert = () => {
    for (const child of inerted) {
      child.removeAttribute('inert');
      child.removeAttribute('aria-hidden');
    }
    inerted.length = 0;
  };

  requestAnimationFrame(() => {
    try {
      surface?.focus({ preventScroll: true });
    } catch (error) {
      surface?.focus();
    }
  });

  let entered = false;

  const restoreFocus = (viaKeyboard) => {
    // Only move focus for people who are actually navigating by keyboard.
    //
    // The skip link is the right landing place for them — it is the first focusable
    // element on the page and is built for exactly this. But focusing it also makes
    // it VISIBLE, because a skip link is styled to appear on focus. Doing that after
    // a mouse click left a "Skip to content" pill parked in the top-left corner of
    // the homepage for every visitor who clicked the tiger. Mouse users need no focus
    // management at all, so they get none.
    if (!viaKeyboard) return;

    const skipLink = document.querySelector('.skip-to-content-link, a[href="#MainContent"]');
    const target = skipLink || document.getElementById('MainContent');
    if (!target) return;
    if (!skipLink && !target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    try {
      target.focus({ preventScroll: true });
    } catch (error) {
      target.focus();
    }
  };

  const finish = (viaKeyboard) => {
    entry.remove();
    root.classList.remove('siw-entry-armed', 'siw-entry-ready');
    releaseInert();
    restoreFocus(viaKeyboard);
    document.dispatchEvent(new CustomEvent('siw:entered'));
  };

  const enter = (viaKeyboard) => {
    if (entered) return;
    entered = true;

    try {
      if (frequency === 'session') window.sessionStorage.setItem(STORAGE_KEY, '1');
      if (frequency === 'once') window.localStorage.setItem(STORAGE_KEY, '1');
    } catch (error) {
      /* Storage unavailable. The entry simply shows again next time. */
    }

    if (prefersReducedMotion.matches) {
      finish(viaKeyboard);
      return;
    }

    entry.classList.add('siw-entry--leaving');

    let settled = false;
    const settle = () => {
      if (settled) return;
      settled = true;
      finish(viaKeyboard);
    };

    entry.addEventListener('transitionend', settle, { once: true });
    // Belt and braces: a dropped transitionend must never strand the visitor.
    window.setTimeout(settle, 700);
  };

  // A click event with detail === 0 was synthesised by the browser from Enter or
  // Space on a focused button — i.e. a keyboard activation, not a pointer one.
  surface?.addEventListener('click', (event) => enter(event.detail === 0));

  entry.addEventListener('keydown', (event) => {
    // Reveal the focus ring only once the visitor has actually reached for the
    // keyboard to navigate. Enter, Space and Escape dismiss the entry anyway, so
    // showing a ring for those would only ever be a flash on the way out.
    if (event.key === 'Tab' || event.key.startsWith('Arrow')) {
      entry.classList.add('siw-entry--keyboard');
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      enter(true);
    }
  });
})();
